/**
 * Client-side media upload helpers (T2.6d/T2.10b — the media plane's browser leg).
 *
 * reencodeImage: canvas re-encode BEFORE upload — strips ALL EXIF (GPS never
 * leaves the device) and converts what the browser can decode (incl. iPhone
 * HEIC on Safari) to JPEG, capped at 2048px. If the browser can't decode the
 * file (e.g. HEIC on Chrome), the original is uploaded and the server-side
 * pipeline gives the verdict.
 *
 * uploadToPresignedPost: the direct-to-S3 upload with the fields the server's
 * presign endpoint returned — bytes never pass through our API.
 */

const MAX_EDGE = 2048;
const JPEG_QUALITY = 0.85;

export interface PresignedUpload {
  url: string;
  fields: Record<string, string>;
}

export const reencodeImage = async (file: File): Promise<{ blob: Blob; contentType: string }> => {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('no 2d context');
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    if (!blob) throw new Error('encode failed');
    return { blob, contentType: 'image/jpeg' };
  } catch {
    // Undecodable in this browser — ship the original; the pipeline decides.
    return { blob: file, contentType: file.type || 'application/octet-stream' };
  }
};

export const uploadToPresignedPost = async (
  upload: PresignedUpload,
  blob: Blob,
  contentType: string,
): Promise<void> => {
  const form = new FormData();
  Object.entries(upload.fields).forEach(([key, value]) => form.append(key, value));
  form.append('Content-Type', contentType);
  form.append('file', blob);

  const response = await fetch(upload.url, { method: 'POST', body: form });
  if (!response.ok) {
    throw new Error(`Upload failed (${response.status})`);
  }
};
